import { Link } from 'react-router-dom';
import { Coins } from 'lucide-react';
import type { Tenant, TenantOverviewDTO } from '../../types/lac';
import { LiksTooltip } from '../../components/liks/LiksTooltip/LiksTooltip';
import { TenantLogo } from '../../features/tenant/components/TenantLogo';
import { useI18n } from '../../i18n/useI18n';
import { resolveLacTenantPath } from '../../routes/lacPaths';
import { LacTenantCostUsageBar } from './LacTenantCostUsageBar';
import { LacTenantRowMetrics } from './LacTenantRowMetrics';
import {
  formatLimitUsage,
  isPositiveLimit,
  tenantDisplayName,
} from './lacTenantRowFormatters';
import './LacTenantRow.css';

type LacTenantRowProps = {
  tenant: Tenant;
  overview?: TenantOverviewDTO | null;
  monthlyEstimatedCosts?: number | null;
  yearlyEstimatedCosts?: number | null;
  monthlyCostLimit?: number | null;
  yearlyCostLimit?: number | null;
};

export function LacTenantRow({
  tenant,
  overview = null,
  monthlyEstimatedCosts = null,
  yearlyEstimatedCosts = null,
  monthlyCostLimit = null,
  yearlyCostLimit = null,
}: LacTenantRowProps) {
  const { locale, translate } = useI18n();
  const displayName = tenantDisplayName(tenant);
  const description = tenant.description?.trim() ?? '';

  const hasMonthlyLimit = isPositiveLimit(monthlyCostLimit);
  const hasYearlyLimit = isPositiveLimit(yearlyCostLimit);
  const hasCostBars = hasMonthlyLimit || hasYearlyLimit;

  const userCount = overview?.userCount ?? 0;
  const maxUsers = overview?.maxUsers ?? tenant.maxUsers;
  const knowledgeBaseCount = overview?.knowledgeBaseCount ?? 0;
  const chatbotCount = overview?.chatbotCount ?? 0;
  const maxChatbots = overview?.maxChatbots ?? tenant.maxChatbots;
  const statusCounts = overview?.crawlJobMetrics?.statusCounts ?? [];

  const monthlyLabel = translate(
    'organisationManagement.costs.estimatedMonthlyCosts',
  );
  const yearlyLabel = translate(
    'organisationManagement.costs.estimatedYearlyCosts',
  );

  const costTooltipContent = [
    hasMonthlyLimit
      ? `${monthlyLabel}: ${formatLimitUsage(monthlyEstimatedCosts, monthlyCostLimit, locale)}`
      : null,
    hasYearlyLimit
      ? `${yearlyLabel}: ${formatLimitUsage(yearlyEstimatedCosts, yearlyCostLimit, locale)}`
      : null,
  ]
    .filter(Boolean)
    .join('\n');

  const costBars = (
    <div className="lac-tenants__cost">
      <Coins className="lac-tenants__metric-icon" strokeWidth={2} aria-hidden />
      <div className="lac-tenants__cost-bars">
        {hasMonthlyLimit ? (
          <LacTenantCostUsageBar
            estimatedCosts={monthlyEstimatedCosts}
            costLimit={monthlyCostLimit}
            ariaLabel={translate(
              'organisationManagement.costs.monthlyLimitUsage',
            )}
          />
        ) : null}
        {hasYearlyLimit ? (
          <LacTenantCostUsageBar
            estimatedCosts={yearlyEstimatedCosts}
            costLimit={yearlyCostLimit}
            ariaLabel={translate(
              'organisationManagement.costs.yearlyLimitUsage',
            )}
          />
        ) : null}
      </div>
    </div>
  );

  return (
    <li className="lac-tenants__item">
      <Link
        to={resolveLacTenantPath(tenant.name)}
        className="lac-tenants__row"
        aria-label={translate('lac.organisations.openProfile', {
          name: displayName,
        })}
      >
        <TenantLogo
          className="tenant-logo--lg"
          logo={tenant.logo}
          label={displayName}
        />

        <div className="lac-tenants__identity">
          <span className="lac-tenants__name">{displayName}</span>
          {description !== '' && (
            <span className="lac-tenants__description">{description}</span>
          )}
        </div>

        <LacTenantRowMetrics
          userCount={userCount}
          maxUsers={maxUsers}
          knowledgeBaseCount={knowledgeBaseCount}
          chatbotCount={chatbotCount}
          maxChatbots={maxChatbots}
          statusCounts={statusCounts}
        />

        <div className="lac-tenants__trailing">
          {hasCostBars ? (
            <LiksTooltip content={costTooltipContent} placement="top-end">
              {costBars}
            </LiksTooltip>
          ) : (
            <div className="lac-tenants__cost" />
          )}

          <span className="lac-tenants__status">
            {tenant.active == null ? (
              translate('lac.organisations.statusUnknown')
            ) : (
              <span
                className={`lac-tenants__badge${
                  tenant.active ? '' : ' lac-tenants__badge--inactive'
                }`}
              >
                {tenant.active
                  ? translate('lac.organisations.active')
                  : translate('lac.organisations.inactive')}
              </span>
            )}
          </span>
        </div>
      </Link>
    </li>
  );
}
